import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthRole } from '@cf-shared/couch-auth';
import { AuthService } from 'services/auth.service';
import { ITestNavigationChildPage } from 'pages/test-navigation-child/test-navigation-child-interface';

interface TestNavigationRootPageParams {
  param1?: string;
  param2?: string;
  param3?: string;
}

@Component({
    selector: 'page-test-navigation-root',
    templateUrl: 'test-navigation-root.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestNavigationRootPage extends CsPage<TestNavigationRootPageParams> {
  // Store param values for display and editing
  param1Value: string = '';
  param2Value: string = '';
  param3Value: string = '';
  currentUrl: string = '';

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Unknown);
  }

  async ionViewWillEnter() {
    // read params from navigation
    this.param1Value = this.navParams.data.param1 || '';
    this.param2Value = this.navParams.data.param2 || '';
    this.param3Value = this.navParams.data.param3 || '';

    // update current URL display
    this.updateCurrentUrl();
    this.cd.markForCheck();
  }

  private updateCurrentUrl() {
    this.currentUrl = this.navCtrl.location.path();
  }

  // called when any param input changes - auto-updates URL
  async handleParamChange() {
    // always provide all params for route parameters
    // use empty string as the value when param is empty (not undefined)
    const params: TestNavigationRootPageParams = {
      param1: this.param1Value || '',
      param2: this.param2Value || '',
      param3: this.param3Value || ''
    };

    // update URL with new params
    await this.navCtrl.updateCurrentPageParamsAsync(params);

    // update URL display
    this.updateCurrentUrl();
    this.cd.markForCheck();
  }

  // open the child page and, while it is still sliding in, change this page's params, the
  // way a tab tap on Home did in df-19cb4bb87306. The child is on the stack before its
  // enter transition and gets its history entry only after it, so waiting for it to appear
  // on the stack lands the update in exactly that window, every time.
  async navigateToChildUpdatingParamsWhileOpening() {
    const depthBefore = this.navCtrl.getPageStack().length;
    const pushing = this.navCtrl.pushAsync(ITestNavigationChildPage, {
      mode: 'test',
      filter: 'all',
      debug: 'false'
    });
    // if the child never appears on the stack, give up after about two seconds of frames
    for (let frame = 0; frame < 120 && this.navCtrl.getPageStack().length <= depthBefore; frame++) {
      await new Promise(resolve => requestAnimationFrame(resolve));
    }
    this.param1Value = 'during-push';
    await this.handleParamChange();
    await pushing;
  }

  // navigate to child page with initial params
  async navigateToChild() {
    await this.navCtrl.pushAsync(ITestNavigationChildPage, {
      mode: 'test',
      filter: 'all',
      debug: 'false'
    });
  }
}
